import { useEffect, useState } from "react";
import { closeModal, Modal, TextInput } from "risa-oasis-ui_v2";
import { BOOptionModel } from "../../../../../../data-model/boOptionModel";
import { ModalId } from "../../../../../../enums/modalId";

interface BoOptionsModalProps {
  data: BOOptionModel | null;
  onModalClose: () => void;
  onSave: (option: BOOptionModel) => Promise<void>;
  mode: "add" | "edit";
  existingOptions: BOOptionModel[];
}

const BoOptionsModal = (props: BoOptionsModalProps) => {
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [priority, setPriority] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    label?: string;
    value?: string;
    priority?: string;
  }>({});

  useEffect(() => {
    if (props.data && props.mode === "edit") {
      setLabel(props.data.label || "");
      setValue(props.data.value || "");
      setPriority(
        props.data.priority !== undefined ? String(props.data.priority) : "",
      );
    } else {
      setLabel("");
      setValue("");
      setPriority("");
    }
    setErrors({});
  }, [props.data, props.mode]);

  const isValidPositiveInteger = (val: string): boolean => {
    if (val === "") return true; // Optional field
    const num = Number(val);
    return Number.isInteger(num) && num >= 0;
  };

  const validateForm = (): boolean => {
    const newErrors: { label?: string; value?: string; priority?: string } = {};

    if (!label.trim()) {
      newErrors.label = "Label is required";
    }

    if (!value.trim()) {
      newErrors.value = "Value is required";
    } else {
      // Validate duplicates for both add and edit (excluding current item in edit mode)
      const duplicateValue = props.existingOptions.find(
        (o) =>
          o.value.toLowerCase() === value.toLowerCase().trim() &&
          (props.mode === "add" || o.value !== props.data?.value),
      );
      if (duplicateValue) {
        newErrors.value = "A BO option with this value already exists";
      }
    }

    if (!isValidPositiveInteger(priority)) {
      newErrors.priority = "Priority must be a non-negative integer";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const option: BOOptionModel = {
        label: label.trim(),
        value: value.trim(),
        ...(priority !== "" && { priority: parseInt(priority, 10) }),
      };
      await props.onSave(option);
      closeModal(ModalId.BO_OPTIONS_MODAL);
    } catch (error) {
      console.error("Error saving option:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    closeModal(ModalId.BO_OPTIONS_MODAL);
    props.onModalClose();
  };

  const handleLabelChange = (data: { name: string; value: string }) => {
    setLabel(data.value);
  };

  const handleValueChange = (data: { name: string; value: string }) => {
    setValue(data.value);
  };

  const handlePriorityChange = (data: { name: string; value: string }) => {
    setPriority(data.value);
  };

  return (
    <Modal
      dialogId={ModalId.BO_OPTIONS_MODAL}
      onSave={handleSave}
      title={props.mode === "add" ? "Add New BO Option" : "Edit BO Option"}
      saveButtonText={
        isSaving ? "Saving..." : props.mode === "add" ? "Add" : "Save"
      }
      cancelText="Cancel"
      disableSave={isSaving || !label.trim() || !value.trim()}
      onClose={handleClose}
    >
      <div className="flex flex-col gap-2">
        <TextInput
          id="label"
          label="Label"
          required
          onChange={handleLabelChange}
          error={errors.label || ""}
          resetField={label === ""}
          defaultValue={label}
        />
        <TextInput
          id="value"
          label="Value"
          required
          onChange={handleValueChange}
          error={errors.value || ""}
          resetField={value === ""}
          defaultValue={value}
        />
        {props.mode === "edit" && (
          <div className="flex flex-col gap-1">
            <TextInput
              id="priority"
              label="Priority"
              onChange={handlePriorityChange}
              error={errors.priority || ""}
              resetField={priority === ""}
              defaultValue={priority}
              readOnly
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BoOptionsModal;
